<script setup lang="ts">
import { computed } from 'vue';
import { useCashback } from '@/composables/useCashback';
import CurrencySymbol from '@/components/Currencies/CurrencySymbol.vue';
import ContainerModal from '@/components/Modal/ContainerModal.vue';

const { getCashbackModal, toSetGetCashbackModal } = useCashback();

const cashback = computed(() => getCashbackModal.value.cashback);

const isVisible = computed({
    get: () => getCashbackModal.value.flag,
    set: (value: boolean) => {
        if (!value) {
            toSetGetCashbackModal(false, null);
        }
    }
});

function handleClose() {
    toSetGetCashbackModal(false, null);
}

const cashbackAmount = computed(() => {
    return cashback.value?.cashback_amount || 0;
});

const currency = computed(() => {
    return cashback.value?.currency || 'USD';
});

const percentage = computed(() => {
    return cashback.value?.cashback_percentage || 0;
});

const maxAmount = computed(() => {
    return cashback.value?.max_cashback_amount || 0;
});

const spent = computed(() => {
    return cashback.value?.real_balance_spent || 0;
});

const won = computed(() => {
    return cashback.value?.real_balance_won || 0;
});

const frequencyText = computed(() => {
    const frequency = cashback.value?.frequency;
    const cashbackText = 'cashback';
    switch (frequency) {
        case 'daily':
            return `daily_${cashbackText}`;
        case 'weekly':
            return `weekly_${cashbackText}`;
        case 'monthly':
            return `monthly_${cashbackText}`;
        default:
            return `${cashbackText}`;
    }
});
</script>

<template>
    <ContainerModal v-model:visible="isVisible" severity="dark" :is-close-button="true" :is-back-button="false" is-border max-width="500px" @close="handleClose" content-bg-color="primary-dark">
        <template #title>
            <div class="cashback-modal__header-content" v-if="cashback">
                <div class="cashback-modal__title-section">
                    <h2 class="cashback-modal__title">
                        {{ $t('cashback_received') }}
                    </h2>
                    <p class="cashback-modal__subtitle">
                        {{ $t(frequencyText) }}
                    </p>
                </div>
            </div>
        </template>

        <template #content>
            <div class="cashback-modal" v-if="cashback">
                <!-- Amount section -->
                <div class="cashback-modal__amount-section">
                    <div class="cashback-modal__amount-label">
                        {{ $t('cashback_amount') }}
                    </div>
                    <div class="cashback-modal__amount">
                        {{ cashbackAmount.toFixed(2) }}
                    </div>
                    <div class="cashback-modal__currency">
                        <CurrencySymbol :code="currency" />
                        <span>{{ currency }}</span>
                    </div>
                </div>

                <!-- Stats grid -->
                <div class="cashback-modal__stats">
                    <div class="cashback-modal__stat-card">
                        <div class="cashback-modal__stat-value">{{ percentage.toFixed(2) }}%</div>
                        <div class="cashback-modal__stat-label">{{ $t('percentage') }}</div>
                    </div>
                    <div class="cashback-modal__stat-card">
                        <div class="cashback-modal__stat-value">${{ maxAmount }}</div>
                        <div class="cashback-modal__stat-label">{{ $t('maximum') }}</div>
                    </div>
                    <div class="cashback-modal__stat-card">
                        <div class="cashback-modal__stat-value">${{ spent }}</div>
                        <div class="cashback-modal__stat-label">{{ $t('spent') }}</div>
                    </div>
                    <div class="cashback-modal__stat-card">
                        <div class="cashback-modal__stat-value">${{ won }}</div>
                        <div class="cashback-modal__stat-label">{{ $t('won') }}</div>
                    </div>
                </div>

                <!-- Period section -->
                <div class="cashback-modal__period">
                    <div class="cashback-modal__period-header">
                        <i class="pi pi-calendar"></i>
                        {{ $t('cashback_period') }}
                    </div>
                    <div class="cashback-modal__period-dates">
                        <span>{{ `${new Date(cashback?.period_start).toLocaleDateString()} ${new Date(cashback?.period_start).toLocaleTimeString()}` }}</span>
                        <i class="pi pi-arrow-right"></i>
                        <span>{{ `${new Date(cashback?.period_end).toLocaleDateString()} ${new Date(cashback?.period_end).toLocaleTimeString()}` }}</span>
                    </div>
                </div>
            </div>
        </template>
    </ContainerModal>
</template>

<style lang="scss" scoped>
.cashback-modal {
    color: var(--white);
    &__header-content {
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        gap: var(--gap-xs);
        .is-mobile & {
            justify-content: flex-start;
            gap: var(--gap-md);
        }
    }

    &__title-section {
        display: flex;
        flex-direction: column;
        gap: var(--gap-xs);
    }

    &__title {
        font-weight: 700;
        margin: 0;
        color: var(--white);
        font-size: 14px;
    }

    &__subtitle {
        font-size: 13px;
        font-weight: 500;
        margin: 0;
        color: rgba(255, 255, 255, 0.8);
        .is-mobile & {
            font-size: 12px;
        }
    }

    &__amount-section {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 20px 16px;
        background: rgba(255, 255, 255, 0.1);
        margin: 16px 0;
        border-radius: 12px;
        backdrop-filter: blur(10px);
    }

    &__amount-label {
        font-size: 12px;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.8);
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 6px;
    }

    &__amount {
        font-size: 36px;
        font-weight: 700;
        color: #10b981;
        margin-bottom: 6px;
    }

    &__currency {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 14px;
        font-weight: 600;
        color: rgba(255, 255, 255, 0.9);
    }

    &__stats {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px;
        margin: 16px 0;
    }

    &__stat-card {
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 8px;
        padding: 12px;
        text-align: center;
        backdrop-filter: blur(10px);
    }

    &__stat-value {
        font-size: 16px;
        font-weight: 700;
        color: var(--white);
        margin-bottom: 3px;
    }

    &__stat-label {
        font-size: 10px;
        font-weight: 500;
        color: rgba(255, 255, 255, 0.7);
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    &__period {
        margin: 16px 0;
        padding: 12px;
        background: rgba(255, 215, 0, 0.1);
        border: 1px solid rgba(255, 215, 0, 0.3);
        border-radius: 8px;
        backdrop-filter: blur(10px);
    }

    &__period-header {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        font-weight: 600;
        color: var(--yellow-hover);
        margin-bottom: 6px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    &__period-dates {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 11px;
        font-weight: 500;
        color: rgba(255, 255, 255, 0.9);

        .pi-arrow-right {
            color: rgba(255, 255, 255, 0.6);
        }
    }

    &__actions {
        display: flex;
        gap: 8px;
    }
}

@media (max-width: 576px) {
    .cashback-modal {
        margin: -8px;
        padding: 8px;
        border-radius: 8px;

        &__amount-section {
            padding: 16px 12px;
        }

        &__stats {
            margin: 12px 0;
        }

        &__period {
            margin: 12px 0;
        }
    }
}
</style>
